import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Appstore,
  setAdvancedSearchIsOpen,
} from "../../../redux";
import StaffButton from "../../staff/Button";
import SearchIcon from "../../Atomos/Icons/Search";
import ProfilePhoto from "../../Atomos/ProfilePhoto";
import PlusIcon from "../../Atomos/Icons/PlusIcon";
import CrossEye from "../../Atomos/Icons/ViewMode/CrossEye";
import {
  fetchMemberSearch,
  MemberSearchFirm,
  MemberSearchJurisdiction,
  MemberSearchProfessional,
  MemberSearchResponse,
} from "../../../Services/MemberSearch";
import {
  ADVANCED_MEMBER_SEARCH_PATH,
  buildMemberSearchPagePath,
  buildProfessionalPath,
  buildFirmPath,
  buildJurisdictionPath,
  getPersonPhotoSrc,
} from "../../../utils/memberSearchNavigation";

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 3;
const MIN_QUERY_MESSAGE = "Please enter at least 3 characters.";
const PREVIEW_LIMIT = 4;
const EXPANDED_LIMIT = 10;

type SearchSectionKey = "professionals" | "firms" | "jurisdictions";

const INITIAL_EXPANDED_SECTIONS: Record<SearchSectionKey, boolean> = {
  professionals: false,
  firms: false,
  jurisdictions: false,
};

const EMPTY_RESULTS: MemberSearchResponse = {
  professionals: { items: [], hasMore: false, total: 0 },
  firms: { items: [], hasMore: false, total: 0 },
  jurisdictions: { items: [], hasMore: false, total: 0 },
};

const JurisdictionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 36 36"
    className="h-10 w-10 shrink-0"
    aria-hidden="true"
  >
    <path
      fill="#BC3323"
      d="M1.3971 15.1038C2.22702 13.2386 3.2993 11.4909 4.58621 9.90599C5.25883 9.0658 5.97784 8.26382 6.73989 7.50381C6.81915 6.68945 7.02648 5.89272 7.35425 5.14304C7.73243 4.2874 8.30996 3.53472 9.03854 2.94794C5.80455 4.77029 3.22368 7.5612 1.65941 10.9276C1.27175 12.2845 1.18226 13.7091 1.3971 15.1038Z"
    />
    <path
      fill="#DD3B33"
      d="M9.52173 7.61422C9.7253 10.3889 10.5994 13.0727 12.0689 15.4351C17.8673 9.09142 25.9781 5.77116 30.8377 7.89723C31.7169 8.28415 32.4857 8.88444 33.0742 9.64365C31.2071 6.3407 28.3306 3.72307 24.8667 2.17479C20.6353 1.00131 14.5884 3.23782 9.52173 7.61422Z"
    />
    <path
      fill="#DD3B33"
      d="M10.136 4.12138C10.0274 4.37127 9.9329 4.62708 9.85303 4.88759C13.4149 2.26452 17.2736 0.794215 20.4627 0.897757C20.2004 0.85634 19.9381 0.814923 19.6758 0.787312C17.3936 0.535815 15.0843 0.730403 12.8765 1.36025C11.6489 1.90411 10.6707 2.88971 10.136 4.12138Z"
    />
    <path
      fill="#9C3325"
      d="M22.0366 33.7346C24.3697 34.77 28.3734 32.9753 31.0103 29.724C31.2761 29.3957 31.5227 29.0523 31.7489 28.6955C29.2984 30.5248 25.343 30.366 21.1047 28.3228C19.9726 30.7595 20.2625 32.9546 22.0366 33.7346Z"
    />
    <path
      fill="#BC3323"
      d="M31.4796 21.9721C29.2293 20.9712 25.4328 22.6141 22.8235 25.6375C26.8685 27.6186 30.6444 27.7567 32.9361 25.9551C33.3641 24.1396 32.8947 22.6003 31.4796 21.9721Z"
    />
    <path
      fill="#9C3325"
      d="M9.2111 31.4221L9.09375 31.3185C9.26907 32.1684 9.6467 32.9635 10.1946 33.6365C10.7426 34.3094 11.4447 34.8403 12.2414 35.1841L12.414 35.2532C13.498 35.5909 14.6123 35.822 15.7412 35.9434C17.1699 36.0946 18.6116 36.0715 20.0347 35.8744C16.9423 36.0125 13.0008 34.4938 9.2111 31.4221Z"
    />
    <path
      fill="#9C3325"
      d="M6.13932 28.5091C4.01722 26.2094 2.34751 23.5303 1.2176 20.6122C0.922302 22.0303 0.941152 23.4958 1.27282 24.9058C2.69219 28.4736 5.23379 31.4829 8.51389 33.4791C7.76959 32.8828 7.1695 32.1261 6.75835 31.2656C6.3472 30.4051 6.1356 29.4628 6.13932 28.5091Z"
    />
    <path
      fill="#9C3325"
      d="M16.2105 25.1888C14.6358 23.9064 13.195 22.4678 11.91 20.8953C10.3055 23.1702 9.2818 25.8031 8.92798 28.5643C9.53543 29.1511 10.1705 29.7171 10.8332 30.2555C13.1379 32.1963 15.8093 33.6541 18.6886 34.5422C16.6868 33.1616 16.6177 30.0622 18.3503 26.7558C17.6324 26.2864 16.9215 25.7618 16.2105 25.1888Z"
    />
    <path
      fill="#BC3323"
      d="M0.465217 17.8304C0.308093 16.9655 0.254794 16.0849 0.306452 15.2073C0.230521 15.6008 0.168395 15.9943 0.126978 16.3946C-0.0343508 17.7705 -0.0343508 19.1605 0.126978 20.5363C0.133426 19.6242 0.246949 18.716 0.465217 17.8304Z"
    />
    <path
      fill="#BC3323"
      d="M34.717 13.5092C34.1831 12.2331 33.1728 11.2153 31.9006 10.6722C27.2412 8.60132 19.3789 11.9768 13.9532 18.2239C15.255 19.8999 16.7442 21.4215 18.3918 22.759C18.9785 23.2353 19.5652 23.6702 20.152 24.0706C23.7346 19.7425 29.1257 17.3679 32.2596 18.7623C34.289 19.6597 34.8964 21.9031 34.1716 24.5538C34.2825 24.3751 34.3839 24.1908 34.4754 24.0015C34.8729 22.8065 35.1459 21.5735 35.2899 20.3223C35.5437 18.0356 35.3491 15.7214 34.717 13.5092Z"
    />
    <path
      fill="#BC3323"
      d="M9.90826 18.1066C8.34474 15.7373 7.28941 13.0696 6.80889 10.2719C6.44304 10.6722 6.08409 11.0933 5.73895 11.5213C4.17278 13.437 2.91301 15.584 2.00452 17.8857C2.97185 20.7506 4.44989 23.4166 6.36711 25.7549C7.001 22.9863 8.20724 20.3811 9.90826 18.1066Z"
    />
  </svg>
);

const MemberSearchModern = () => {
  const { advancedSearchIsOpen } = useSelector((state: Appstore) => state.menus);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<MemberSearchResponse>(EMPTY_RESULTS);
  const [expandedSections, setExpandedSections] = useState(INITIAL_EXPANDED_SECTIONS);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    dispatch(setAdvancedSearchIsOpen(false));
  }, [pathname, dispatch]);

  useEffect(() => {
    if (!advancedSearchIsOpen) {
      setIsDropdownOpen(false);
      return;
    }
    const timer = window.setTimeout(() => inputRef.current?.focus(), 150);
    return () => window.clearTimeout(timer);
  }, [advancedSearchIsOpen]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setExpandedSections(INITIAL_EXPANDED_SECTIONS);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!advancedSearchIsOpen || debouncedQuery.length < MIN_QUERY_LENGTH) {
      abortRef.current?.abort();
      setResults(EMPTY_RESULTS);
      setIsLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;
    setIsLoading(true);

    fetchMemberSearch(debouncedQuery, {
      limit: EXPANDED_LIMIT,
      signal: controller.signal,
      quickSearch: true,
    })
      .then((data) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResults(data);
        setIsDropdownOpen(true);
      })
      .catch((error: Error) => {
        if (requestId !== requestIdRef.current || error.name === "AbortError") {
          return;
        }
        setResults(EMPTY_RESULTS);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  }, [debouncedQuery, advancedSearchIsOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const trimmedQueryLength = query.trim().length;
  const showMinLengthWarning =
    trimmedQueryLength > 0 && trimmedQueryLength < MIN_QUERY_LENGTH;

  const showDropdown =
    advancedSearchIsOpen &&
    isDropdownOpen &&
    debouncedQuery.length >= MIN_QUERY_LENGTH;

  const hasResults =
    results.professionals.items.length > 0 ||
    results.firms.items.length > 0 ||
    results.jurisdictions.items.length > 0;

  const closeSearch = useCallback(() => {
    dispatch(setAdvancedSearchIsOpen(false));
    setIsDropdownOpen(false);
  }, [dispatch]);

  const handleNavigate = (path: string | null) => {
    if (!path) {
      return;
    }
    navigate(path);
    closeSearch();
  };

  const handleSearchSubmit = () => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return;
    }
    closeSearch();
    navigate(buildMemberSearchPagePath(trimmed));
  };

  const handleToggleSection = (section: SearchSectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    setIsDropdownOpen(true);
  };

  const getSectionItems = <T,>(section: SearchSectionKey, items: T[]): T[] => {
    const displayLimit = expandedSections[section] ? EXPANDED_LIMIT : PREVIEW_LIMIT;
    return items.slice(0, displayLimit);
  };

  const hasMoreThanExpandedLimit = (hasMore: boolean, itemCount: number) =>
    hasMore || itemCount > EXPANDED_LIMIT;

  const shouldShowViewMore = (section: SearchSectionKey, hasMore: boolean, itemCount: number) => {
    if (expandedSections[section]) {
      return false;
    }
    return hasMore || itemCount > PREVIEW_LIMIT;
  };

  const renderSectionAction = (
    section: SearchSectionKey,
    hasMore: boolean,
    itemCount: number,
  ) => {
    const isExpanded = expandedSections[section];

    if (!isExpanded) {
      if (!shouldShowViewMore(section, hasMore, itemCount)) {
        return null;
      }
      return (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-Default hover:underline"
          onClick={() => handleToggleSection(section)}
        >
          <span className="inline-flex h-4 w-4 shrink-0 text-Default [&_path]:fill-current [&_svg]:h-full [&_svg]:w-full">
            <PlusIcon />
          </span>
          + View more
        </button>
      );
    }

    if (hasMoreThanExpandedLimit(hasMore, itemCount)) {
      return (
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-Default hover:underline"
          onClick={handleSearchSubmit}
        >
          <span className="inline-flex h-4 w-4 shrink-0 text-Default [&_path]:fill-current [&_svg]:h-full [&_svg]:w-full">
            <PlusIcon />
          </span>
          Show all
        </button>
      );
    }

    return (
      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-Default hover:underline"
        onClick={() => handleToggleSection(section)}
      >
        <span className="inline-flex h-4 w-4 shrink-0 text-Default [&_path]:fill-current [&_svg]:h-full [&_svg]:w-full">
          <CrossEye />
        </span>
        Hide
      </button>
    );
  };

  const renderProfessional = (person: MemberSearchProfessional) => {
    const photoSrc = getPersonPhotoSrc(person);
    return (
      <button
        key={person.id}
        type="button"
        onClick={() =>
          handleNavigate(
            buildProfessionalPath(person, { highlight: debouncedQuery }),
          )
        }
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-gray-100">
          <ProfilePhoto src={photoSrc} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-neutral-dark">
            {person.name} {person.lastName}
          </div>
          {person.subtitle ? (
            <div className="truncate text-sm text-gray-font">{person.subtitle}</div>
          ) : null}
        </div>
      </button>
    );
  };

  const renderFirm = (firm: MemberSearchFirm) => {
    const logo = firm.smallLogo || firm.logo;
    return (
      <button
        key={firm.id}
        type="button"
        onClick={() => handleNavigate(buildFirmPath(firm))}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-white">
          {logo ? (
            <img src={logo} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs font-bold text-gray-font">{firm.name.slice(0, 2)}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold text-neutral-dark">{firm.name}</div>
          {firm.subtitle ? (
            <div className="truncate text-sm text-gray-font">{firm.subtitle}</div>
          ) : null}
        </div>
      </button>
    );
  };

  const renderJurisdiction = (jurisdiction: MemberSearchJurisdiction) => (
    <button
      key={jurisdiction.id}
      type="button"
      onClick={() => handleNavigate(buildJurisdictionPath(jurisdiction))}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
    >
      <JurisdictionIcon />
      <div className="min-w-0 flex-1">
        <div className="truncate font-bold text-neutral-dark">{jurisdiction.name}</div>
        {jurisdiction.firmName ? (
          <div className="truncate text-sm text-gray-font">{jurisdiction.firmName}</div>
        ) : null}
      </div>
    </button>
  );

  const renderSection = (section: SearchSectionKey, title: string, items: ReactNode[], hasMore: boolean, itemCount: number) => {
    if (items.length === 0) {
      return null;
    }
    return (
      <div>
        <div className="flex items-center gap-3 px-4 py-2">
          <span className="shrink-0 text-sm font-medium text-gray-font">{title}</span>
          <span className="h-px flex-1 bg-gray-200" aria-hidden="true" />
          {renderSectionAction(section, hasMore, itemCount)}
        </div>
        {items}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`${
        advancedSearchIsOpen
          ? "pointer-events-auto translate-y-0 opacity-100"
          : "pointer-events-none -translate-y-5 opacity-0"
      } absolute left-0 right-0 top-4 z-50 w-full max-w-5xl transform rounded-md border border-gray-200 bg-white shadow-sm transition-all duration-300 ease-in-out lg:left-auto lg:right-0`}
    >
      <div className="relative flex flex-col gap-4 px-4 py-3 sm:px-5 lg:flex-row lg:flex-nowrap lg:items-center lg:gap-0 lg:pl-6 lg:pr-2 lg:py-2">
        <div className="shrink-0 whitespace-nowrap text-base font-bold text-[#1F1F1F] md:text-lg">
          Member Search
        </div>

        <div className="relative min-w-0 w-full lg:ml-6 lg:flex-1">
          <div className={`flex h-11 w-full items-center gap-2.5 rounded-md bg-[#F6F6F6] px-3 focus-within:outline focus-within:outline-1 focus-within:outline-Default md:h-12 md:gap-3 md:px-4 ${showMinLengthWarning ? "errorInput" : ""}`}>
            <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-gray-font md:h-5 md:w-5 [&_svg]:h-full [&_svg]:w-full">
              <SearchIcon />
            </span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder=""
              onChange={(event) => {
                setQuery(event.target.value);
                setIsDropdownOpen(true);
              }}
              onFocus={() => setIsDropdownOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSearchSubmit();
                }
                if (event.key === "Escape") {
                  closeSearch();
                }
              }}
              className="min-w-0 flex-1 bg-transparent text-base text-neutral-dark outline-none md:text-lg"
            />
          </div>
          {showMinLengthWarning ? (
            <span className="mt-2 flex flex-wrap items-center text-base font-medium text-primary">
              {MIN_QUERY_MESSAGE}
            </span>
          ) : null}

          {showDropdown ? (
            <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-50 max-h-[min(28rem,50vh)] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg sm:max-h-[28rem]">
              {isLoading && !hasResults ? (
                <div className="px-4 py-6 text-sm text-gray-font">Searching...</div>
              ) : !hasResults ? (
                <p className="px-4 py-6 text-sm text-gray-font">
                  No records found! Please refine your search query.
                </p>
              ) : (
                <>
                  {renderSection(
                    "professionals",
                    "Professionals",
                    getSectionItems("professionals", results.professionals.items).map(
                      renderProfessional,
                    ),
                    results.professionals.hasMore,
                    results.professionals.items.length,
                  )}
                  {renderSection(
                    "firms",
                    "Firms",
                    getSectionItems("firms", results.firms.items).map(renderFirm),
                    results.firms.hasMore,
                    results.firms.items.length,
                  )}
                  {renderSection(
                    "jurisdictions",
                    "Jurisdictions",
                    getSectionItems("jurisdictions", results.jurisdictions.items).map(
                      renderJurisdiction,
                    ),
                    results.jurisdictions.hasMore,
                    results.jurisdictions.items.length,
                  )}
                </>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex w-full items-center gap-6 lg:contents">
          <StaffButton
            text="Search"
            color="neutral"
            variant="outlined"
            size="fixed"
            icon={
              <span className="inline-flex h-4 w-4 items-center justify-center [&_svg]:h-full [&_svg]:w-full">
                <SearchIcon />
              </span>
            }
            onClick={handleSearchSubmit}
            className="min-w-0 flex-1 !h-11 !rounded-md !bg-white !px-3 !py-0 !text-gray-font !outline-gray-font hover:!bg-[#F6F6F6] hover:!text-gray-font text-sm sm:!px-4 md:!h-12 md:text-base lg:ml-4 lg:shrink-0 lg:flex-none lg:!min-w-[8.5rem] lg:!px-4"
          />
          <StaffButton
            text="Advanced Search"
            color="primary"
            variant="filled"
            size="fixed"
            onClick={() => {
              closeSearch();
              navigate(ADVANCED_MEMBER_SEARCH_PATH);
            }}
            className="min-w-0 flex-1 !h-11 !rounded-md !px-3 !py-0 text-sm sm:!px-4 md:!h-12 md:text-base lg:ml-6 lg:shrink-0 lg:flex-none lg:!min-w-[11rem] lg:!px-4"
          />
        </div>
      </div>
    </div>
  );
};

export default MemberSearchModern;

